import { Box, useTheme } from '@mui/material';
import { createColumnHelper } from '@tanstack/react-table';
import { useMemo, useState } from 'react';
import { TableHeaderCell } from '../../../components/Table/CellRenderers/TableHeaderCell';
import { MonthlyTimesheet, SelectItem, TimesheetStatus, User, UserWithTsPeriodData } from '../../../types';
import { AvatarCell } from '../../../components/Table/CellRenderers/AvatarCell';
import { getMonthLabelShort } from '../../../utils/formatters';
import { TSTableCheckbox } from '../components/TSTableCheckbox';
import { AccountantStatusCell } from '../components/AccountantStatusCell';
import { useAtomValue } from 'jotai';
import { isSmallScreenState } from '../../../state/UIState';
import TooltipComponent from '../../../components/TooltipComponent/TooltipComponent';
import { Typography } from '../../../components/Typography/Typography';
import { ReactComponent as ClockLoaderIcon } from '../../../assets/icons/clock-loader.svg';
import { StatusBadge } from '../../../components/StatusBadge/StatusBadge';

const columnHelper = createColumnHelper<UserWithTsPeriodData>();

export const useAccountantTsStatusesColumns = (timesheets: User[]) => {
  const { colors } = useTheme();
  const isSmallScreen = useAtomValue(isSmallScreenState);

  const userWithLargestTimesheet = timesheets.reduce((prevUser, currentUser) => {
    if (
      (currentUser?.monthlyTimesheets || []).length > (prevUser?.monthlyTimesheets || []).length
    ) {
      return currentUser;
    } else {
      return prevUser;
    }
  }, timesheets[0]);

  const inactiveIds = useMemo(
    () => new Set(timesheets.filter((user) => user.isActive === false).map((user) => user.id)),
    [timesheets]
  );

  const columns = useMemo(() => {
    const fixedColumns = [
      columnHelper.display({
        id: 'select',
        header: ({ table }) => {
          return (
            <TSTableCheckbox
              inHeader
              {...{
                checked: table.getIsAllRowsSelected(),
                indeterminate: table.getIsSomeRowsSelected(),
                onChange: table.getToggleAllRowsSelectedHandler(),
              }}
            />
          );
        },
        cell: ({ row }) => {
          return (
            <div>
              <TSTableCheckbox
                row={row}
                {...{
                  checked: row.getIsSelected(),
                  disabled: !row.getCanSelect(),
                  indeterminate: row.getIsSomeSelected(),
                  onChange: () => null,
                }}
              />
            </div>
          );
        },
        meta: {
          width: isSmallScreen ? '42px' : '70px',
          maxWidth: isSmallScreen ? '42px' : '70px',
          minWidth: isSmallScreen ? '42px' : '70px',
        },
      }),
      columnHelper.accessor('name', {
        cell: (info) => (
          <AvatarCell
            avatarBoxStyle={{
              background: colors.surfaceIndicator.needsReview,
            }}
            avatarLetterStyle={{
              color: colors.text.main,
            }}
            name={info.getValue()}
          />
        ),
        header: () => <TableHeaderCell text='Submitter' />,
        sortingFn: (rowA, rowB) => {
          const valueA = rowA.original?.name || '';
          const valueB = rowB.original?.name || '';
          return valueA.localeCompare(valueB);
        },
        filterFn: (row, id, filterValue: SelectItem[]) => {
          if (!filterValue.length) return true;
          return filterValue.some((user) => user.id === row.original.id);
        },
        meta: {
          width: isSmallScreen ? '180px' : 'auto',
          maxWidth: isSmallScreen ? '180px' : 'auto',
          minWidth: isSmallScreen ? '180px' : 'auto',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
      columnHelper.display({
        id: 'status',
        cell: ({ row }) => (
          <StatusBadge isActive={!inactiveIds.has(row.original.id)} style={{ width: 'fit-content' }} />
        ),
        header: () => <TableHeaderCell text='Status' />,
        meta: {
          width: '120px',
          maxWidth: '120px',
          minWidth: '120px',
          thStyles: { paddingLeft: '16px' },
          tdStyles: { paddingLeft: '16px' },
        },
      }),
    ];

    const periodCols = (userWithLargestTimesheet?.monthlyTimesheets || [])
      .map((ts) => `${ts.year}-${ts.month}`)
      .flatMap((period) => {
        return columnHelper.accessor(period, {
          cell: (info) => {
            return (
              <AccountantStatusCell
                timesheet={info.getValue() as MonthlyTimesheet}
                operatingPartners={timesheets}
              />
            );
          },
          header: () => {
            const [year, month] = period.split('-');
            const monthLabel = getMonthLabelShort(Number(month));

            const nonClosedCount = timesheets.reduce((count, user) => {
              const monthlyTimesheet = user.monthlyTimesheets?.find(
                ts => ts.year === Number(year) && ts.month === Number(month)
              );
              if (monthlyTimesheet && monthlyTimesheet.status !== TimesheetStatus.CLOSED) {
                return count + 1;
              }
              return count;
            }, 0);

            return <MonthHeader label={`${monthLabel} ${year}`} count={nonClosedCount} />;
          },
          meta: {
            width: isSmallScreen ? '130px' : '200px',
            maxWidth: isSmallScreen ? '130px' : '200px',
            minWidth: isSmallScreen ? '130px' : '200px',
            alignHeader: 'right',
            thStyles: { borderLeft: 'none' },
            tdStyles: { borderLeft: 'none' },
          },
          enableSorting: false,
        });
      });
    return [...fixedColumns, ...periodCols];
  }, [
    colors.surfaceIndicator.needsReview,
    colors.text.main,
    inactiveIds,
    isSmallScreen,
    timesheets,
    userWithLargestTimesheet?.monthlyTimesheets,
  ]);

  return columns;
};

type MonthHeaderProps = {
  label: string;
  count: number;
};

export const MonthHeader = ({ label, count }: MonthHeaderProps) => {
  const { colors } = useTheme();
  const [open, setOpen] = useState(false);

  return (
    <Box display='flex' alignItems='center' gap='4px' justifyContent='space-between'>
      <Typography variant='subtitle1' color={colors.text.main} style={{ letterSpacing: '0.25px' }}>
        {label}
      </Typography>
      <TooltipComponent
        open={open}
        onOpen={() => setOpen(true)}
        onClose={() => setOpen(false)}
        placement='top'
        content={
          <Typography variant='body' color={colors.text.main}>
            Open/Pending Timesheets
          </Typography>
        }
      >
        <Box
          display='flex'
          alignItems='center'
          gap='4px'
          onMouseEnter={() => setOpen(true)}
          onMouseLeave={() => setOpen(false)}
        >
          <ClockLoaderIcon />
          <Typography variant='body' color={colors.text.main}>
            {count}
          </Typography>
        </Box>
      </TooltipComponent>
    </Box>
  );
};
